import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import { useCountriesOptions } from '@/api/countries';
import { useMyIpLocation } from '@/api/me';
import { getMyTenantsQueryOptions } from '@/api/tenant';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MapBox } from '@/components/ui/map';
import { Select } from '@/components/ui/select';
import {
  AddressAutocomplete,
  type OnSelectSuggestion,
} from '@/features/estates/components/address-autocomplete';

import { useCreateSubTenant } from '../../api/subtenants';
import { getTenantOptions } from '../../api/tenant';
import type { CreateSubTenantType } from '../../schema/create-subtenant';
import { getCreateSubTenantSchema } from '../../schema/create-subtenant';
import type { useModal } from '@/components/modals/new/use-modal';
import { Modal } from '@/components/modals/new/modal';

export interface AddSubtenantModalProps {
  tenantId: string;
  modal: ReturnType<typeof useModal>;
}

export const AddSubtenantModal: React.FC<AddSubtenantModalProps> = ({ tenantId, modal }) => {
  const { t } = useTranslation();
  const { data: countries } = useCountriesOptions();
  const queryClient = useQueryClient();
  const { data: ipLocation } = useMyIpLocation();

  const { mutate } = useCreateSubTenant({
    mutationConfig: {
      onSuccess: () => {
        // TODO: change message
        toast.success(t('successfully-invited-contact'));
        queryClient.invalidateQueries({ queryKey: getTenantOptions(tenantId).queryKey });
        queryClient.invalidateQueries({ queryKey: getMyTenantsQueryOptions().queryKey });
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const schema = getCreateSubTenantSchema();
  const form = useForm<CreateSubTenantType>({
    resolver: zodResolver(schema),
  });

  const latitude = form.watch('addressData.lat');
  const longitude = form.watch('addressData.long');

  const onSelectSuggestion: OnSelectSuggestion = ({
    houseNumber,
    city,
    street,
    address,
    latitude,
    longitude,
    countryId,
    postalCode,
    countryCode,
  }) => {
    form.reset((prev) => ({
      ...prev,
      addressData: {
        ...prev.addressData,
        ...(address && { addressAutoComplete: address }),
        street: street ?? '',
        houseNumber: houseNumber ?? '',
        city: city ?? '',
        postalCode: postalCode ?? '',
        ...(address && { address }),
        ...(countryId && { countryId }),
        ...(latitude && { lat: latitude }),
        ...(longitude && { long: longitude }),
        ...(countryCode && { countryCode }),
      },
    }));
  };

  const onSubmit = (data: CreateSubTenantType) => {
    mutate({
      ...data,
      tenantId,
    });
    modal.dismiss();
  };

  return (
    <Modal {...modal} className="min-w-[360px]" title={t('add-subtenant')}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Modal.Body>
            <div className="no-scrollbar max-h-[34rem] overflow-auto">
              <div className="w-full rounded-md border border-background-lighter">
                <p className="w-full rounded-t-md bg-background-lighter px-10 py-2">
                  {t('general')}
                </p>
                <div className="px-10 pb-8 pt-5">
                  <Controller
                    name="tenantData.name"
                    control={form.control}
                    render={({ field, formState }) => (
                      <Input
                        {...field}
                        label={t('name')}
                        errors={[formState.errors.tenantData?.name?.message || '']}
                      />
                    )}
                  />
                  {/* TODO: add more fields if necessary */}
                </div>
              </div>

              <div className="z-50 w-full rounded-md border border-background-lighter">
                <p className="w-full rounded-t-md bg-background-lighter px-10 py-2">
                  {t('address')}
                </p>
                <div className="px-10 pb-8 pt-5">
                  <Controller
                    name="addressData.addressAutoComplete"
                    control={form.control}
                    render={({ field, formState }) => (
                      <AddressAutocomplete
                        {...field}
                        size="lg"
                        startIcon={<Search color="#9CA3AF" />}
                        label={t('address')}
                        onSelectSuggestion={onSelectSuggestion}
                        errors={[formState.errors.addressData?.addressAutoComplete?.message || '']}
                      />
                    )}
                  />
                  <div className="mt-4 size-full h-48 overflow-hidden rounded bg-black">
                    <MapBox
                      key={latitude ? `${latitude}:${longitude}` : ipLocation?.latitude}
                      zoom={12}
                      {...(latitude && longitude
                        ? {
                            latitude: Number(latitude),
                            longitude: Number(longitude),
                          }
                        : ipLocation
                          ? {
                              latitude: ipLocation.latitude,
                              longitude: ipLocation.longitude,
                            }
                          : undefined)}
                    />
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-x-10">
                    <Controller
                      name="addressData.houseNumber"
                      control={form.control}
                      render={({ field, formState }) => (
                        <Input
                          {...field}
                          size="lg"
                          label={t('houseNumber')}
                          errors={[formState.errors.addressData?.houseNumber?.message || '']}
                        />
                      )}
                    />
                    <Controller
                      name="addressData.street"
                      control={form.control}
                      render={({ field, formState }) => (
                        <Input
                          {...field}
                          size="lg"
                          label={t('street')}
                          errors={[formState.errors.addressData?.street?.message || '']}
                        />
                      )}
                    />
                    <Controller
                      name="addressData.countryId"
                      control={form.control}
                      render={({ field, formState }) => (
                        <Select
                          {...field}
                          size="lg"
                          label={t('country')}
                          options={countries || []}
                          errors={[formState.errors.addressData?.countryId?.message || '']}
                        />
                      )}
                    />
                    <Controller
                      name="addressData.city"
                      control={form.control}
                      render={({ field, formState }) => (
                        <Input
                          {...field}
                          size="lg"
                          label={t('city')}
                          errors={[formState.errors.addressData?.city?.message || '']}
                        />
                      )}
                    />
                    <Controller
                      name="addressData.postalCode"
                      control={form.control}
                      render={({ field, formState }) => (
                        <Input
                          {...field}
                          size="lg"
                          label={t('postalCode')}
                          errors={[formState.errors.addressData?.postalCode?.message || '']}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Actions className="flex items-center justify-end gap-4 py-4">
            <div className="flex items-center justify-end gap-4 py-4">
              <Button variant="outline" type="button" onClick={modal.dismiss}>
                {t('close')}
              </Button>
              <Button variant="contained" type="submit">
                {t('submit')}
              </Button>
            </div>
          </Modal.Actions>
        </form>
      </FormProvider>
    </Modal>
  );
};
